import classNames from 'classnames';
import {
  ContentBlock,
  FlexContainer,
  Txt,
  LoadingPlaceholder,
} from 'components';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { useProperty } from 'state';
import { formatCurrency } from 'helpers/formatNumber';
import { fromSwiftDate } from 'helpers/swiftDateFormatter';
import styles from './EPCDetails.module.scss';

/**
 * EPCDetails
 *
 * @return {JSX.Element}
 */
const EPCDetails = ({ className }) => {
  const { epcData } = useProperty();
  useMemo(
    () => !epcData.isInitialized && !epcData.isLoading && epcData.load(),
    [epcData]
  );

  const dataState = epcData.data;

  return (
    <FlexContainer
      className={classNames(
        styles.EPCDetails,
        className,
        EPCDetails.displayName
      )}
      column
    >
      <FlexContainer className={styles.Header}>
        <Txt xxxx>EPC Details</Txt>
      </FlexContainer>
      <FlexContainer column gap className={styles.Body}>
        {epcData.isLoading ? (
          <LoadingPlaceholder spinner />
        ) : epcData.error ? (
          <Txt>{epcData.error}</Txt>
        ) : (
          <>
            <Txt xx>Current EPC</Txt>
            <ContentBlock row gap>
              <FlexContainer column gapHalf flex>
                <Txt caption>Expires on</Txt>
                <Txt xxx>
                  {DateTime.fromJSDate(
                    fromSwiftDate(dataState.expiresOn)
                  ).toLocaleString(DateTime.DATE_FULL)}
                </Txt>
                <Txt regular>
                  Updated:{' '}
                  {DateTime.fromJSDate(
                    fromSwiftDate(dataState.dateUpdated)
                  ).toLocaleString(DateTime.DATETIME_MED)}
                </Txt>
              </FlexContainer>
              <FlexContainer
                className={classNames(
                  styles.Level,
                  dataState.energyRating === 'E' && styles.Level__E,
                  (dataState.energyRating === 'F' ||
                    dataState.energyRating === 'G') &&
                    styles.Level__FG
                )}
                alignCenter
                justifyContentCenter
              >
                <Txt xxx>{dataState.energyRating}</Txt>
              </FlexContainer>
            </ContentBlock>
            {dataState.epcs && dataState.epcs.length ? (
              <>
                <Txt xx>EPC History</Txt>
                {dataState.epcs.map((currentEpc) => (
                  <ContentBlock column gap className={styles.HistoryItem}>
                    <FlexContainer row gap>
                      <FlexContainer column gapHalf flex>
                        <Txt caption>Expires on</Txt>
                        <Txt xxx>
                          {DateTime.fromJSDate(
                            fromSwiftDate(currentEpc.expiresOn)
                          ).toLocaleString(DateTime.DATE_FULL)}
                        </Txt>
                        <Txt regular>
                          Inspected on:{' '}
                          {DateTime.fromJSDate(
                            fromSwiftDate(currentEpc.dateUpdated)
                          ).toLocaleString(DateTime.DATETIME_MED)}
                        </Txt>
                      </FlexContainer>
                      <FlexContainer
                        className={classNames(
                          styles.Level,
                          dataState.energyRating === 'E' && styles.Level__E,
                          (dataState.energyRating === 'F' ||
                            dataState.energyRating === 'G') &&
                            styles.Level__FG
                        )}
                        alignCenter
                        justifyContentCenter
                      >
                        <Txt xxx>{currentEpc.energyRating}</Txt>
                      </FlexContainer>
                    </FlexContainer>
                    <FlexContainer gap spaceBetween>
                      <Txt>Estimated heating cost</Txt>
                      <Txt>
                        £{formatCurrency(currentEpc.estimatedHeatingCost)}
                      </Txt>
                    </FlexContainer>
                    <FlexContainer gap spaceBetween>
                      <Txt>Estimated hot water cost</Txt>
                      <Txt>
                        £{formatCurrency(currentEpc.estimatedHotWaterCost)}
                      </Txt>
                    </FlexContainer>
                    <FlexContainer gap spaceBetween>
                      <Txt>Estimated lighting cost</Txt>
                      <Txt>
                        £{formatCurrency(currentEpc.estimatedLightingCost)}
                      </Txt>
                    </FlexContainer>
                    <FlexContainer gap spaceBetween>
                      <Txt>Floor space</Txt>
                      <Txt>{currentEpc.floorArea}sqm</Txt>
                    </FlexContainer>
                    <FlexContainer gap spaceBetween>
                      <Txt>Efficiency</Txt>
                      <Txt>{currentEpc.energyEfficiency}/100</Txt>
                    </FlexContainer>
                  </ContentBlock>
                ))}
              </>
            ) : null}
          </>
        )}
      </FlexContainer>
    </FlexContainer>
  );
};

EPCDetails.displayName = 'EPCDetails';

export default observer(EPCDetails);
